import { useEffect, useMemo, useRef, useState } from "react";
import { fetchCsrAssistAnswers } from "./csrAssistApi";
import type { CsrAssistEnquiry } from "./csrAssistApi";
import {
  buildCsrEnquiries,
  CsrAssistInput,
  normalizeQuestionKey,
  stableQuestionId,
} from "./csrAssistLogic";

const ANSWER_DEBOUNCE_MS = 800;

export function useCsrAssist(input: CsrAssistInput) {
  const [answerByQuestion, setAnswerByQuestion] = useState<
    Record<string, string>
  >({});
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const inflightKeyRef = useRef("");

  const assistTurns = useMemo(
    () =>
      input.transcript.filter(
        (turn) => !turn.streaming && Boolean(turn.text?.trim())
      ),
    [input.transcript]
  );

  const enquiryItems = useMemo(
    () => buildCsrEnquiries(input),
    [
      input.activeFormId,
      input.claimFields,
      input.dynamicFormSchema,
      input.dynamicFieldStatuses,
      assistTurns,
    ]
  );

  const enquiries: CsrAssistEnquiry[] = useMemo(
    () =>
      enquiryItems.map((item) => {
        const key = normalizeQuestionKey(item.question);
        const answer = answerByQuestion[key] ?? "";
        return {
          id: stableQuestionId(item.question, item.kind),
          question: item.question,
          answer,
          kind: item.kind,
          status: answer ? "ready" : "loading",
        };
      }),
    [enquiryItems, answerByQuestion]
  );

  const pendingQuestions = useMemo(() => {
    return enquiryItems
      .map((item) => item.question)
      .filter((question) => !answerByQuestion[normalizeQuestionKey(question)]);
  }, [enquiryItems, answerByQuestion]);

  const pendingKey = useMemo(
    () =>
      pendingQuestions
        .map((q) => normalizeQuestionKey(q))
        .join("|"),
    [pendingQuestions]
  );

  const inputSignature = useMemo(
    () =>
      [
        input.activeFormId ?? "menu",
        JSON.stringify(input.claimFields ?? {}),
        input.dynamicFormSchema?.formHeading ?? "",
        JSON.stringify(input.dynamicFieldStatuses ?? {}),
        assistTurns.map((t) => `${t.speaker}:${t.text}`).join("|"),
      ].join("::"),
    [input, assistTurns]
  );

  useEffect(() => {
    if (!assistTurns.length || !pendingQuestions.length) {
      setLoadingAnswers(false);
      return;
    }

    if (inflightKeyRef.current === pendingKey) {
      return;
    }

    const timer = window.setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      inflightKeyRef.current = pendingKey;
      setLoadingAnswers(true);
      setError(null);

      try {
        const result = await fetchCsrAssistAnswers(
          assistTurns,
          pendingQuestions,
          input.activeFormId
        );
        if (requestId !== requestIdRef.current) return;

        setAnswerByQuestion((prev) => {
          const next = { ...prev };
          result.enquiries.forEach((item, index) => {
            const question = pendingQuestions[index] ?? item.question;
            const key = normalizeQuestionKey(question);
            if (item.answer?.trim()) {
              next[key] = item.answer.trim();
            }
          });
          return next;
        });
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        const message =
          err instanceof Error ? err.message : "Could not load CSR answers";
        setError(message);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingAnswers(false);
          inflightKeyRef.current = "";
        }
      }
    }, ANSWER_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [assistTurns, pendingKey, pendingQuestions, inputSignature, input.activeFormId]);

  return {
    enquiries,
    loading: loadingAnswers,
    error,
    hasConversation: assistTurns.length > 0,
    questionCount: enquiryItems.length,
  };
}
